import 'whatwg-fetch';
import React, { useState, useRef, useEffect, useCallback } from 'react';

const DEBOUNCE_MS = 200;
const MIN_QUERY_LENGTH = 3;
const MAX_SUGGESTIONS = 5;

function AddressAutocomplete({ value, onChange, onSelect, onBlur, id, name, autoComplete }) {
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const abortRef = useRef(null);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);
  const skipNextFetchRef = useRef(false);

  const fetchSuggestions = useCallback(async query => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const response = await fetch('/api/address/autocomplete', {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        body: JSON.stringify({ query }),
        signal: controller.signal
      });
      const data = await response.json();
      if (data.status === 200 && Array.isArray(data.suggestions)) {
        setSuggestions(data.suggestions.slice(0, MAX_SUGGESTIONS));
        setIsOpen(data.suggestions.length > 0);
        setActiveIndex(-1);
      } else {
        setSuggestions([]);
        setIsOpen(false);
      }
    } catch (e) {
      // aborted or network error — leave current state
    }
  }, []);

  useEffect(() => {
    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false;
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const query = (value || '').trim();
    if (query.length < MIN_QUERY_LENGTH) {
      setSuggestions([]);
      setIsOpen(false);
      return;
    }
    debounceRef.current = setTimeout(() => fetchSuggestions(query), DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [value, fetchSuggestions]);

  useEffect(() => {
    const handleClickOutside = e => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = suggestion => {
    skipNextFetchRef.current = true;
    setIsOpen(false);
    setSuggestions([]);
    setActiveIndex(-1);
    onSelect({
      street1: suggestion.primary_line,
      city: suggestion.city,
      state: suggestion.state,
      zipCode: suggestion.zip_code
    });
  };

  const handleKeyDown = e => {
    if (!isOpen || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(i => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      handleSelect(suggestions[activeIndex]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const listboxId = `${id}-listbox`;

  return (
    <div className="AddressAutocomplete" ref={containerRef}>
      <input
        id={id}
        name={name}
        type="text"
        placeholder=" "
        autoComplete={autoComplete}
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        onBlur={onBlur}
        onKeyDown={handleKeyDown}
        onFocus={() => { if (suggestions.length > 0) setIsOpen(true); }}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={isOpen}
        aria-controls={listboxId}
        aria-activedescendant={activeIndex >= 0 ? `${listboxId}-${activeIndex}` : undefined}
      />
      {isOpen && suggestions.length > 0 && (
        <ul
          id={listboxId}
          role="listbox"
          className="AddressAutocomplete__list"
        >
          {suggestions.map((s, i) => (
            <li
              id={`${listboxId}-${i}`}
              key={`${s.primary_line}-${s.zip_code}-${i}`}
              role="option"
              aria-selected={i === activeIndex}
              className={`AddressAutocomplete__item${i === activeIndex ? ' AddressAutocomplete__item--active' : ''}`}
              onMouseDown={e => { e.preventDefault(); handleSelect(s); }}
              onMouseEnter={() => setActiveIndex(i)}
            >
              <span className="AddressAutocomplete__primary">{s.primary_line}</span>
              <span className="AddressAutocomplete__secondary">{s.city}, {s.state} {s.zip_code}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default AddressAutocomplete;
